<template>
  <div class="page-wrapper">
    <section class="payment grid-layout">
      <div class="payment__main card grid-layout__main">
        <h1 class="payment__title">{{ $t('payment.title') }}</h1>
        <h2 class="payment__subtitle title-medium">
          {{ $t('payment.contactInformation') }}
        </h2>
        <PersonalInfoForm
          idPrefix="payingCustomer"
          :user="formUser"
          :disabled="isRebooking"
          @update:user="updateFormUser"
        />
        <div class="payment__passenger-container">
          <Checkbox
            id="buyingForSomeoneElse"
            v-model="buyingForSomeoneElse"
            :disabled="isRebooking"
          >
            {{ $t('payment.buyingForOthers') }}
          </Checkbox>
          <div v-if="buyingForSomeoneElse">
            <h2 class="payment__subtitle title-medium">
              {{ $t('payment.passengerContactInfo') }}
            </h2>
            <p class="payment__help-text body-large">
              {{ $t('payment.passengerHelpText') }}
            </p>
            <PersonalInfoForm
              idPrefix="passenger"
              :user="passengerFormComputed"
              :disabled="isRebooking"
              @update:user="updatePassengerForm"
            />
          </div>
        </div>
        <PaymentVoucher
          :voucherCode="voucherCode"
          :vouchers="vouchers"
          :bookingNumber="bookingNumber"
          @update:voucherCode="voucherCode = $event"
          @addVoucher="addVoucher"
          @removeVoucher="removeVoucher"
        />
        <PaymentMethodes
          v-if="showPaymentMethods && totalAmount > 0"
          :paymentMethods="paymentMethods"
          :selectedPaymentMethod="selectedPaymentMethod"
          @update:selectedPaymentMethod="
            (value) => {
              selectedPaymentMethod = value
            }
          "
        />
        <AdyenCardPayment
          v-if="
            selectedPaymentMethod === 'ADYEN_ADYEN_CC_DIRECT' && totalAmount > 0
          "
          :amount="Number(totalAmount)"
          :currency="'NOK'"
          :bookingNumber="bookingNumber"
          :customerEmail="formUser.email"
          :customerName="`${formUser.first_name} ${formUser.last_name}`"
          @card-data="handleCardData"
          @card-error="handleCardError"
        />
      </div>
      <div class="payment__sidebar grid-layout__sidebar">
        <div class="payment-page__my-travel">
          <MyTravel
            v-if="!isRebooking"
            :showTotalPrice="true"
            :overrideTotalPrice="
              bookingStore.bookingData?.total_price_to_be_paid !== undefined
                ? {
                    amount: Number(
                      bookingStore.bookingData.total_price_to_be_paid
                    ),
                    currency: 'NOK',
                  }
                : undefined
            "
          />
          <template v-if="isRebooking">
            <MyPastTravel />
            <MyRebooking />
          </template>
        </div>
      </div>

      <NavigationBar>
        <div class="payment__nav-bar">
          <PriceTag
            :price="totalAmount"
            :is-rebooking="isRebooking"
          />
          <button
            class="button button--primary button--medium payment__submit"
            :disabled="
              isProcessingPayment || (!selectedPaymentMethod && totalAmount > 0)
            "
            @click="submitPaymentHandler"
          >
            <span v-if="!isProcessingPayment">
              {{ submitButtonText }}
            </span>
            <span
              v-else
              class="payment__submit-loading"
            >
              <span class="spinner"></span>
              {{ $t('payment.methods.processing') }}
            </span>
          </button>
        </div>
      </NavigationBar>
    </section>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, onMounted, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useUserStore } from '@/stores/userStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { useAuthStore } from '@/stores/oauthStore'
  import NavigationBar from '@/components/TravelPlanner/NavigationBar/NavigationBar.vue'
  import PersonalInfoForm from '@/components/BaseComponents/PersonalInfoForm.vue'
  import Checkbox from '@/components/BaseComponents/Checkbox.vue'
  import MyTravel from '@/components/TravelPlanner/MyTravel/MyTravel.vue'
  import MyPastTravel from '@/components/TravelPlanner/MyTravel/MyPastTravel.vue'
  import MyRebooking from '@/components/TravelPlanner/MyTravel/MyRebooking.vue'
  import PaymentVoucher from '@/components/TravelPlanner/Payment/PaymentVoucher.vue'
  import PaymentMethodes from '@/components/TravelPlanner/Payment/PaymentMethodes.vue'
  import PriceTag from '@/components/TravelPlanner/PriceTag.vue'
  import AdyenCardPayment from '@/components/Payment/AdyenCardPayment.vue'
  import {
    fetchPaymentMethods,
    submitCustomerInfo as submitCustomerInfoService,
    updatePassengerInfo,
    submitPayment,
    confirmBooking,
    processPayment,
    submitCardPayment,
    fetchBookingData,
    addVoucher as addVoucherService,
    requestRefund,
  } from '@/services/journeyService'
  import { getUserDetails } from '@/services/userService'
  import { getToken } from '@/services/tokenService'
  import { sleep } from '@/utils/common'

  export default defineComponent({
    name: 'PaymentPage',
    components: {
      PersonalInfoForm,
      NavigationBar,
      Checkbox,
      MyTravel,
      MyPastTravel,
      MyRebooking,
      PaymentVoucher,
      PaymentMethodes,
      PriceTag,
      AdyenCardPayment,
    },
    setup() {
      const journeyStore = useJourneyStore()
      const userStore = useUserStore()
      const bookingStore = useBookingStore()
      const errorStore = useErrorStore()
      const myTravelStore = useMyTravelStore()
      const authStore = useAuthStore()
      const router = useRouter()
      const route = useRoute()
      const { t } = useI18n()

      const bookingNumber = computed(() => {
        const fromStore = bookingStore.bookingNumber
        const fromBookingData = bookingStore.bookingData?.booking_number
        const fromUrl = route.query.bookingNumber as string

        const result = fromStore || fromBookingData || fromUrl

        return result
      })

      const isRebooking = computed(() => {
        const urlParam = route.query.isRebooking === 'true'
        const storeFlag = bookingStore.isRebooking
        const hasOriginalPrice = !!myTravelStore.originalBookingPrice?.amount

        return urlParam || storeFlag || hasOriginalPrice
      })
      const isProcessingPayment = ref(false)
      const buyingForSomeoneElse = ref(false)

      const formUser = ref({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
      })
      const updateFormUser = (userData: any) => {
        formUser.value = {
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          phone_number: userData.phone_number || '',
        }
      }

      const passengerForm = ref({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
      })
      const updatePassengerForm = (userData: any) => {
        passengerForm.value = {
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          phone_number: userData.phone_number || '',
        }
      }

      const passengerFormComputed = computed(() => ({
        ...passengerForm.value,
        phone_number: passengerForm.value.phone_number || '',
      }))

      const voucherCode = ref('')
      const vouchers = ref<string[]>([])
      const paymentMethods = ref<any[]>([])
      const selectedPaymentMethod = ref<string>('ADYEN_ADYEN_CC_DIRECT')

      const totalAmount = computed(() => {
        if (paymentScenario.value === 'confirm_only') {
          return 0
        }

        if (bookingStore.bookingData?.total_price_to_be_paid !== undefined) {
          const price = Number(
            bookingStore.bookingData.total_price_to_be_paid || 0
          )
          return price
        }

        const totalPrice = myTravelStore.totalPriceToBePaid
        if (!totalPrice) {
          return 0
        }

        const price = totalPrice.amount || 0
        return price
      })

      const cardData = ref<any>(null)
      const cardError = ref<any>(null)
      const handleCardData = (data: any) => {
        cardData.value = data
        cardError.value = null
      }
      const handleCardError = (error: any) => {
        cardError.value = error
        cardData.value = null
      }

      const paymentScenario = computed<
        'confirm_only' | 'additional_payment' | 'refund'
      >(() => {
        if (!bookingStore.bookingData) return 'confirm_only'

        let amountToPay = Number(
          bookingStore.bookingData.total_price_to_be_paid || 0
        )

        if (amountToPay < 0) {
          return 'refund'
        }
        if (amountToPay === 0) {
          return 'confirm_only'
        }
        if (amountToPay > 0) {
          return 'additional_payment'
        }
        return 'confirm_only'
      })

      const showPaymentMethods = computed(
        () =>
          (!isRebooking.value ||
            paymentScenario.value === 'additional_payment') &&
          totalAmount.value > 0
      )

      const submitButtonText = computed(() => {
        if (isProcessingPayment.value) return t('payment.processing')
        if (paymentScenario.value === 'refund') {
          return t('payment.methods.processRefund', 'Process Refund')
        }
        return paymentScenario.value === 'confirm_only'
          ? t('common.actions.confirm')
          : t('payment.methods.payNow')
      })

      const addVoucher = () => {
        if (voucherCode.value) {
          vouchers.value.push(voucherCode.value)
          voucherCode.value = ''
        }
      }
      const removeVoucher = (index: number) => {
        vouchers.value.splice(index, 1)
      }
      const processPaymentWithRetries = async (
        bookingNumber: string,
        paymentReference: string,
        bookingToken: string,
        processData?: any,
        maxRetries: number = 5,
        delayMs: number = 2000
      ) => {
        let lastError = null

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const result = await processPayment(
              bookingNumber,
              paymentReference,
              bookingToken,
              processData
            )
            return result
          } catch (error) {
            lastError = error
            console.error(
              `[PaymentPage] Payment attempt ${attempt} failed:`,
              error
            )
            if (attempt < maxRetries) {
              await sleep(delayMs)
            }
          }
        }
        console.error(`[PaymentPage] All ${maxRetries} payment attempts failed`)
        throw lastError
      }

      const submitPaymentHandler = async () => {
        if (!bookingNumber.value) {
          errorStore.setError(t('payment.error.noBooking'))
          return
        }

        if (
          paymentScenario.value !== 'confirm_only' &&
          !selectedPaymentMethod.value &&
          totalAmount.value > 0
        ) {
          errorStore.setError(t('payment.error.selectMethod'))
          return
        }

        if (
          selectedPaymentMethod.value === 'ADYEN_ADYEN_CC_DIRECT' &&
          totalAmount.value > 0 &&
          (!cardData.value || cardError.value)
        ) {
          errorStore.setError(t('payment.error.invalidCard'))
          return
        }

        isProcessingPayment.value = true
        errorStore.clearError()

        try {
          // Validate form data
          if (
            !formUser.value.first_name ||
            !formUser.value.last_name ||
            !formUser.value.email
          ) {
            throw new Error(t('payment.error.missingCustomerInfo'))
          }
          if (
            buyingForSomeoneElse.value &&
            (!passengerForm.value.first_name ||
              !passengerForm.value.last_name ||
              !passengerForm.value.email)
          ) {
            throw new Error(t('payment.error.missingPassengerInfo'))
          }

          // Ensure booking token
          let bookingToken = localStorage.getItem('token') || (await getToken())
          if (!bookingToken) {
            throw new Error(t('payment.error.tokenFetchFailed'))
          }
          localStorage.setItem('bookingToken', bookingToken)

          // Save form data and booking context
          localStorage.setItem(
            'paymentFormData',
            JSON.stringify({
              formUser: formUser.value,
              passengerForm: passengerForm.value,
              buyingForSomeoneElse: buyingForSomeoneElse.value,
              bookingNumber: bookingNumber.value,
              bookingData: bookingStore.bookingData,
            })
          )
          const hasAttemptedPaymentBefore = localStorage.getItem(
            'hasAttemptedPaymentBefore'
          )
          localStorage.setItem('hasAttemptedPaymentBefore', 'true')

          // Handle customer and passenger info
          if (!isRebooking.value && !hasAttemptedPaymentBefore) {
            const customerData = {
              customer: {
                first_name: formUser.value.first_name.trim(),
                last_name: formUser.value.last_name.trim(),
                email: formUser.value.email.trim(),
                phone_number: formUser.value.phone_number?.trim() || '',
                crm_reference: userStore.user?.id?.toString(),
              },
            }
            const customerResponse = await submitCustomerInfoService(
              bookingNumber.value,
              customerData
            )
            if (!customerResponse?.data?.booking) {
              throw new Error(t('payment.error.customerInfo'))
            }
            bookingStore.setBookingData(customerResponse.data.booking)

            const bookingPassengers = bookingStore.bookingData?.passengers || []
            if (!bookingPassengers.length) {
              throw new Error(t('payment.error.noPassengers'))
            }
            const passengerId = bookingPassengers[0].id
            const passengerData = {
              id: passengerId,
              first_name: (buyingForSomeoneElse.value
                ? passengerForm.value.first_name
                : formUser.value.first_name
              ).trim(),
              last_name: (buyingForSomeoneElse.value
                ? passengerForm.value.last_name
                : formUser.value.last_name
              ).trim(),
              email: (buyingForSomeoneElse.value
                ? passengerForm.value.email
                : formUser.value.email
              ).trim(),
              phone_number: formUser.value.phone_number?.trim() || '',
            }
            const passengerResponse = await updatePassengerInfo(
              bookingNumber.value,
              [passengerData]
            )
            if (!passengerResponse?.data) {
              throw new Error(t('payment.error.passengerInfo'))
            }
          }

          if (paymentScenario.value === 'additional_payment') {
            if (selectedPaymentMethod.value === 'ADYEN_ADYEN_CC_DIRECT') {
              const paymentData = {
                booking_number: bookingNumber.value,
                payment_method_code: selectedPaymentMethod.value,
                currency_code: 'NOK',
                amount: totalAmount.value,
                encrypted_card_number: cardData.value?.encrypted_card_number,
                encrypted_expiry_month: cardData.value?.encrypted_expiry_month,
                encrypted_expiry_year: cardData.value?.encrypted_expiry_year,
                encrypted_security_code:
                  cardData.value?.encrypted_security_code,
                return_url: `${window.location.origin}/reiseplanlegger/betaling?bookingNumber=${bookingNumber.value}`,
              }

              const response = await submitCardPayment(paymentData)

              if (response?.data?.payment_result?.status === 'success') {
                const paymentReference = response.data.payment_result.reference
                const result = await processPayment(
                  bookingNumber.value,
                  paymentReference,
                  bookingToken
                )
                if (result?.data?.payment_result?.status === 'success') {
                  localStorage.removeItem('pendingPayment')
                  await router.push(`/bekreftelse/${bookingNumber.value}`)
                } else {
                  throw new Error(t('payment.error.paymentProcessing'))
                }
              } else if (
                response?.data?.payment_result?.status === 'pending' &&
                response.data.payment_result.redirect_url
              ) {
                const redirectUrl = response.data.payment_result.redirect_url
                const postData =
                  response.data.payment_result.redirect_post_data || []
                const paymentReference = response.data.payment_result.reference

                if (!redirectUrl || !paymentReference) {
                  throw new Error(t('payment.error.noRedirectUrl'))
                }

                localStorage.setItem(
                  'pendingPayment',
                  JSON.stringify({
                    bookingNumber: bookingNumber.value,
                    paymentReference,
                    bookingToken,
                    paymentMethod: selectedPaymentMethod.value,
                    timestamp: Date.now(),
                  })
                )

                const baseRedirectUrl = redirectUrl.split('?')[0]
                const urlParams = new URLSearchParams(
                  redirectUrl.split('?')[1] || ''
                )
                const paReq = urlParams.get('PaReq') || ''

                const form = document.createElement('form')
                form.method = 'POST'
                form.action = baseRedirectUrl
                form.style.display = 'none'

                const fields = [
                  { name: 'PaReq', value: paReq },
                  { name: 'TermUrl', value: paymentData.return_url },
                  ...postData,
                ]

                fields.forEach(({ name, value }) => {
                  const input = document.createElement('input')
                  input.type = 'hidden'
                  input.name = name
                  input.value = encodeURIComponent(value)
                  form.appendChild(input)
                })

                document.body.appendChild(form)
                form.submit()
              } else {
                throw new Error(
                  t('payment.error.paymentFailed', {
                    message: response?.data?.message || 'Unknown error',
                  })
                )
              }
            } else if (selectedPaymentMethod.value === 'ADYEN_VIPPS') {
              const returnUrlBase =
                process.env.VUE_APP_PAYMENT_RETURN_URL || window.location.origin
              const returnUrl = `${returnUrlBase}/reiseplanlegger/betaling?bookingNumber=${encodeURIComponent(
                bookingNumber.value
              )}`
              const telephoneNumber =
                formUser.value?.phone_number?.trim() ||
                passengerForm.value?.phone_number?.trim() ||
                ''
              if (!telephoneNumber) {
                throw new Error(t('payment.error.missingPhoneNumber'))
              }

              const paymentData = {
                booking_number: bookingNumber.value,
                payment_method_code: selectedPaymentMethod.value,
                return_url: returnUrl,
                psp_payment_details: {
                  channel: 'Web',
                  telephone_number: telephoneNumber,
                },
              }

              const response = await submitPayment(paymentData)
              if (!response?.data?.payment_result) {
                throw new Error(
                  t('payment.error.paymentFailed', {
                    message: 'No payment result from server',
                  })
                )
              }

              const paymentResult = response.data.payment_result
              const redirectUrl = paymentResult.redirect_url
              const reference = paymentResult.reference

              if (!redirectUrl || !reference) {
                throw new Error(t('payment.error.noRedirectUrl'))
              }

              localStorage.setItem(
                'pendingPayment',
                JSON.stringify({
                  bookingNumber: paymentData.booking_number,
                  paymentReference: reference,
                  bookingToken: bookingToken,
                  paymentMethod: selectedPaymentMethod.value,
                  timestamp: Date.now(),
                })
              )

              window.location.href = redirectUrl
            }
          } else if (paymentScenario.value === 'refund') {
            const confirmResponse = await confirmBooking(bookingNumber.value, {
              triggerBookingCommunication: true,
            })

            if (!confirmResponse?.data?.booking) {
              throw new Error(
                t(
                  'payment.error.confirmBooking',
                  'Failed to confirm booking before refund'
                )
              )
            }

            const refundResponse = await requestRefund(
              bookingNumber.value,
              'psp',
              undefined,
              'ANY'
            )

            if (!refundResponse?.data?.booking) {
              throw new Error(
                t('payment.error.refundFailed', 'Refund processing failed')
              )
            }

            bookingStore.setBookingData(refundResponse.data.booking)
            localStorage.removeItem('pendingPayment')
            localStorage.removeItem('paymentFormData')

            await router.push(`/bekreftelse/${bookingNumber.value}`)
          } else {
            const confirmResponse = await confirmBooking(bookingNumber.value, {
              triggerBookingCommunication: true,
            })
            if (!confirmResponse?.data?.booking) {
              throw new Error(t('payment.error.confirmBooking'))
            }
            bookingStore.setBookingData(confirmResponse.data.booking)
            localStorage.removeItem('pendingPayment')
            localStorage.removeItem('paymentFormData')
            await router.push(`/bekreftelse/${bookingNumber.value}`)
          }
        } catch (error) {
          console.error('submitPaymentHandler error:', error)
          let errorMessage = t('payment.error.unknown')
          if (error instanceof Error) {
            errorMessage = error.message || errorMessage
          } else if (typeof error === 'string') {
            errorMessage = error
          } else if (error && typeof error === 'object' && 'message' in error) {
            errorMessage = String(error.message) || errorMessage
          }
          errorStore.setError(
            `${t('payment.error.paymentFailed')}: ${errorMessage}`
          )
        } finally {
          isProcessingPayment.value = false
        }
      }

      onMounted(async () => {
        try {
          // Restore form data and booking context
          const savedFormData = localStorage.getItem('paymentFormData')
          let journeyData, myTravelData
          if (savedFormData) {
            const {
              formUser: savedFormUser,
              passengerForm: savedPassengerForm,
              buyingForSomeoneElse: savedBuyingForSomeoneElse,
              bookingNumber: savedBookingNumber,
              bookingData,
            } = JSON.parse(savedFormData)
            formUser.value = savedFormUser || formUser.value
            passengerForm.value = savedPassengerForm || passengerForm.value
            buyingForSomeoneElse.value =
              savedBuyingForSomeoneElse || buyingForSomeoneElse.value
            bookingNumber.value = savedBookingNumber || bookingNumber.value
            if (bookingData) {
              bookingStore.setBookingData(bookingData)
            }
            journeyData = { bookingData }
          }

          if (route.query.bookingNumber && !bookingNumber.value) {
            bookingNumber.value = route.query.bookingNumber
            bookingStore.setBookingNumber(route.query.bookingNumber)
          }
          if (
            route.query.isRebooking === 'true' ||
            myTravelStore.originalBookingPrice?.amount
          ) {
            bookingStore.setIsRebooking(true)
          }

          if (userStore.isLoggedIn && userStore.token && !userStore.user) {
            const userDetails = await getUserDetails(userStore.token)
            userStore.setUser(userDetails?.data?.customer)
          }

          if (!bookingNumber.value) {
            errorStore.setError(t('payment.error.noBooking'))
            console.error('No booking number available')
            router.push({ name: 'Search' })
            return
          }

          // Handle Vipps cancellation
          if (route.query.status === 'cancelled') {
            errorStore.setError(t('payment.error.paymentCancelled'))
            try {
              const [bookingResponse, paymentMethodsResponse] =
                await Promise.all([
                  fetchBookingData(bookingNumber.value),
                  fetchPaymentMethods(bookingNumber.value),
                ])
              if (!bookingResponse?.data?.booking) {
              } else {
                bookingStore.setBookingData(bookingResponse.data.booking)
              }
              paymentMethods.value = paymentMethodsResponse || []
            } catch (error) {
              console.error('Cancellation handling error:', error)
              router.push({ name: 'Search' })
            }
            return
          }

          if (
            (route.query.MD && route.query.PaRes) ||
            route.query.redirectResult
          ) {
            const pendingPayment = localStorage.getItem('pendingPayment')
            if (!pendingPayment) {
              errorStore.setError(t('payment.error.noPendingPayment'))
              return
            }

            let paymentData
            try {
              paymentData = JSON.parse(pendingPayment)
            } catch (error) {
              errorStore.setError(t('payment.error.invalidPendingPayment'))
              return
            }

            const {
              bookingNumber: pendingBookingNumber,
              paymentReference,
              bookingToken,
              paymentMethod,
            } = paymentData
            if (!pendingBookingNumber || !paymentReference || !bookingToken) {
              errorStore.setError(t('payment.error.incompletePendingPayment'))
              return
            }

            isProcessingPayment.value = true
            try {
              let processData
              if (
                route.query.MD &&
                route.query.PaRes &&
                paymentMethod === 'ADYEN_ADYEN_CC_DIRECT'
              ) {
                processData = { payment_session: route.query.MD }
              } else if (route.query.redirectResult) {
                processData = { token: route.query.redirectResult }
              }

              const result = await processPayment(
                pendingBookingNumber,
                paymentReference,
                bookingToken,
                processData
              )

              const isSuccess =
                result?.data?.payment_result?.status === 'success' ||
                result?.data?.status === 'success' ||
                result?.data?.result === 'success' ||
                result?.data?.payment_result?.status === 'completed'

              if (isSuccess) {
                const bookingResponse = await fetchBookingData(
                  pendingBookingNumber
                )
                bookingStore.setBookingData(
                  bookingResponse?.data?.booking || {
                    bookingNumber: pendingBookingNumber,
                    bookingToken,
                  }
                )
                localStorage.removeItem('pendingPayment')
                localStorage.removeItem('paymentFormData')
                await router.push(`/bekreftelse/${pendingBookingNumber}`)
              } else {
                const errorMessage =
                  result?.data?.message ||
                  result?.message ||
                  t('payment.error.cancelled')
                const translatedMessage =
                  paymentMethod === 'ADYEN_VIPPS'
                    ? t('payment.error.vippsFailed')
                    : t('payment.error.paymentFailed')
                errorStore.setError(`${translatedMessage}: ${errorMessage}`)
                // Attempt to restore booking
                const [bookingResponse, paymentMethodsResponse] =
                  await Promise.all([
                    fetchBookingData(pendingBookingNumber),
                    fetchPaymentMethods(pendingBookingNumber),
                  ])
                if (!bookingResponse?.data?.booking) {
                  if (journeyData && myTravelData) {
                  } else {
                    throw new Error(
                      t('payment.error.bookingNotFound', {
                        bookingNumber: pendingBookingNumber,
                      })
                    )
                  }
                } else {
                  bookingStore.setBookingData(bookingResponse.data.booking)
                }
                paymentMethods.value = paymentMethodsResponse || []
              }
            } catch (error) {
              console.error('Payment processing error:', error)
              const translatedMessage =
                paymentMethod === 'ADYEN_VIPPS'
                  ? t('payment.error.vippsFailed')
                  : t('payment.error.paymentFailed')
              let errorMessage = t('payment.error.unknown')
              if (error instanceof Error) {
                errorMessage = error.message || errorMessage
              } else if (typeof error === 'string') {
                errorMessage = error
              } else if (
                error &&
                typeof error === 'object' &&
                'message' in error
              ) {
                errorMessage = String(error.message) || errorMessage
              }
              errorStore.setError(`${translatedMessage}: ${errorMessage}`)
              // Attempt to restore booking
              const [bookingResponse, paymentMethodsResponse] =
                await Promise.all([
                  fetchBookingData(pendingBookingNumber),
                  fetchPaymentMethods(pendingBookingNumber),
                ])
              if (!bookingResponse?.data?.booking) {
                if (journeyData && myTravelData) {
                } else {
                  throw new Error(
                    t('payment.error.bookingNotFound', {
                      bookingNumber: pendingBookingNumber,
                    })
                  )
                }
              } else {
                bookingStore.setBookingData(bookingResponse.data.booking)
              }
              paymentMethods.value = paymentMethodsResponse || []
            } finally {
              isProcessingPayment.value = false
            }
            return
          }

          const [bookingResponse, paymentMethodsResponse] = await Promise.all([
            fetchBookingData(bookingNumber.value),
            fetchPaymentMethods(bookingNumber.value),
          ])
          if (!bookingResponse?.data?.booking) {
          } else {
            bookingStore.setBookingData(bookingResponse.data.booking)
          }
          paymentMethods.value = paymentMethodsResponse || []
        } catch (error) {
          console.error('onMounted error:', error)
          errorStore.setError(t('payment.error.initFailed'))
          router.push({ name: 'Search' })
        }
      })

      watch(
        () => userStore.user,
        (newUser) => {
          if (newUser) {
            const preferredPhone =
              newUser.phone_numbers?.find((p) => p.preferred)?.value ||
              newUser.phone_numbers?.[0]?.value ||
              ''
            if (
              !formUser.value.first_name ||
              !formUser.value.last_name ||
              !formUser.value.email
            ) {
              updateFormUser({
                first_name: newUser.first_name || '',
                last_name: newUser.last_name || '',
                email: newUser.email || '',
                phone_number: preferredPhone,
              })
            }
          }
        },
        { immediate: true }
      )

      watch(
        () => bookingStore.bookingData,
        (bookingData) => {
          if (bookingData) {
            myTravelStore.restoreStateFromLocalStorage()
            myTravelStore.updateDiscountsAndTotal(bookingData)
          }
        },
        { immediate: true, deep: true }
      )

      return {
        bookingStore,
        myTravelStore,
        formUser,
        updateFormUser,
        buyingForSomeoneElse,
        passengerForm,
        updatePassengerForm,
        passengerFormComputed,
        voucherCode,
        vouchers,
        addVoucher,
        removeVoucher,
        paymentMethods,
        selectedPaymentMethod,
        isProcessingPayment,
        submitPaymentHandler,
        isRebooking,
        showPaymentMethods,
        totalAmount,
        paymentScenario,
        bookingNumber,
        submitButtonText,
        cardData,
        cardError,
        handleCardData,
        handleCardError,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .payment {
    color: $clr-text;
    text-align: left;
    margin: 2rem 0 7rem;

    &__title {
      @include title-large;
      color: $clr-primary;
      margin: 0;
    }

    &__subtitle {
      margin: 1.563rem 0;
    }

    &__form {
      display: flex;
      flex-direction: column;
    }

    &__payment,
    &__success {
      margin-top: 1.25rem;
    }

    &__main {
      display: flex;
      flex-direction: column;
      padding: 1.875rem;

      @media screen and (max-width: $breakpoint-sm) {
        order: 2;
      }
    }

    @media screen and (max-width: $breakpoint-sm) {
      &__sidebar {
        order: 1;
      }
    }

    &__passenger-container {
      padding: 1rem;
      margin: 1.563rem 0;
      border: 1px solid $clr-primary-100;
      border-radius: $border-radius;
    }

    &__price {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
      @include body-large;

      span {
        line-height: 1;
        margin-left: 0.5rem;
      }
    }

    &__button {
      background-color: $clr-primary;
      color: $clr-white;
      align-self: flex-start;
    }

    &__nav-bar {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
    }

    &__submit {
      margin-left: 1rem;

      &-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
      }
    }
  }

  .spinner {
    display: inline-block;
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #fff;
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
